"use client";

/**
 * BroadcastChannel utility for cross-tab communication
 * Handles sign-out events across all browser tabs and windows
 */

export type BroadcastMessage = {
  type: "SIGN_OUT" | "SIGN_IN" | "SESSION_UPDATE";
  payload?: Record<string, unknown>;
  timestamp: number;
};

class BroadcastChannelManager {
  private channel: BroadcastChannel | null = null;
  private listeners: Map<string, (message: BroadcastMessage) => void> =
    new Map();

  constructor() {
    if (typeof window !== "undefined") {
      try {
        this.channel = new BroadcastChannel("kriya-auth");
        this.channel.onmessage = this.handleMessage.bind(this);
      } catch (error) {
        console.error("Failed to create BroadcastChannel:", error);
      }
    }

    // Bind methods to ensure proper context
    this.broadcastSignOut = this.broadcastSignOut.bind(this);
    this.broadcastSignIn = this.broadcastSignIn.bind(this);
    this.broadcastSessionUpdate = this.broadcastSessionUpdate.bind(this);
    this.sendMessage = this.sendMessage.bind(this);
    this.addListener = this.addListener.bind(this);
  }

  private handleMessage(event: MessageEvent<BroadcastMessage>) {
    const message = event.data;

    // Call all registered listeners for this message type
    this.listeners.forEach((listener, key) => {
      if (key.startsWith(message.type)) {
        listener(message);
      }
    });
  }

  /**
   * Check if the broadcast channel is ready
   */
  public isReady(): boolean {
    return this.channel !== null;
  }

  /**
   * Send a message to all other tabs/windows
   */
  public sendMessage(
    type: BroadcastMessage["type"],
    payload?: Record<string, unknown>,
  ) {
    if (!this.channel) {
      console.warn("BroadcastChannel not available");
      return;
    }

    const message: BroadcastMessage = {
      type,
      payload,
      timestamp: Date.now(),
    };

    try {
      this.channel.postMessage(message);
      console.log("BroadcastChannel message sent:", message);
    } catch (error) {
      console.error("Failed to send BroadcastChannel message:", error);
    }
  }

  /**
   * Listen for specific message types
   */
  public addListener(
    messageType: BroadcastMessage["type"],
    listener: (message: BroadcastMessage) => void,
    id?: string,
  ) {
    const key = `${messageType}_${id || Math.random().toString(36).substr(2, 9)}`;
    this.listeners.set(key, listener);

    return () => {
      this.listeners.delete(key);
    };
  }

  /**
   * Send sign-out message to all tabs
   */
  public broadcastSignOut(reason?: string) {
    if (!this.channel) {
      console.warn("BroadcastChannel not ready, skipping sign-out broadcast");
      return;
    }
    this.sendMessage("SIGN_OUT", { reason });

    // Also store in localStorage as a fallback for tabs that might miss the broadcast
    if (typeof window !== "undefined") {
      try {
        localStorage.setItem(
          "kriya-signout-event",
          JSON.stringify({
            type: "SIGN_OUT",
            reason,
            timestamp: Date.now(),
          }),
        );
      } catch (error) {
        console.error("Failed to store sign-out event in localStorage:", error);
      }
    }
  }

  /**
   * Send sign-in message to all tabs
   */
  public broadcastSignIn(userId?: string) {
    this.sendMessage("SIGN_IN", { userId });
  }

  /**
   * Send session update message to all tabs
   */
  public broadcastSessionUpdate(sessionData?: Record<string, unknown>) {
    this.sendMessage("SESSION_UPDATE", sessionData);
  }

  /**
   * Check for stored sign-out events (fallback mechanism)
   */
  public checkStoredSignOutEvent(): BroadcastMessage | null {
    if (typeof window === "undefined") return null;

    try {
      const storedEvent = localStorage.getItem("kriya-signout-event");
      if (storedEvent) {
        const event = JSON.parse(storedEvent);
        // Clear the stored event after reading it
        localStorage.removeItem("kriya-signout-event");
        return event;
      }
    } catch (error) {
      console.error("Failed to check stored sign-out event:", error);
    }

    return null;
  }

  /**
   * Clean up the broadcast channel
   */
  public destroy() {
    if (this.channel) {
      this.channel.close();
      this.channel = null;
    }
    this.listeners.clear();
  }
}

// Create a singleton instance
export const broadcastChannel = new BroadcastChannelManager();

// Export individual functions for convenience
export const sendMessage = (
  ...args: Parameters<typeof broadcastChannel.sendMessage>
) => broadcastChannel.sendMessage(...args);
export const addListener = (
  ...args: Parameters<typeof broadcastChannel.addListener>
) => broadcastChannel.addListener(...args);
export const broadcastSignOut = (
  ...args: Parameters<typeof broadcastChannel.broadcastSignOut>
) => broadcastChannel.broadcastSignOut(...args);
export const broadcastSignIn = (
  ...args: Parameters<typeof broadcastChannel.broadcastSignIn>
) => broadcastChannel.broadcastSignIn(...args);
export const broadcastSessionUpdate = (
  ...args: Parameters<typeof broadcastChannel.broadcastSessionUpdate>
) => broadcastChannel.broadcastSessionUpdate(...args);
export const destroyBroadcastChannel = () => broadcastChannel.destroy();
